import DestinationPicker from "../components/destinationPicker.jsx";
import DeparturePicker from "../components/departurePicker.jsx";
import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker from "../components/datePicker.jsx";
import PaxPicker from "../components/paxPicker.jsx";
import PackageDatasource from "../datasources/packageDatasource.js";
import PopoverInput from "../components/inner/popoverInput.jsx";
import useWindowDimensions from "../helpers/Hooks/useWindowDimensions";
import useJsonCookie from "../helpers/Hooks/useJsonCookie.jsx";
import { format, parse, startOfTomorrow } from "date-fns";
import { DEFAULT_ROOMS, serializeRooms, deserializeRooms } from "../helpers/rooms.js";
import { computeStartWindow, buildBookingUrl } from "../helpers/bookingUrl.js";

import { IsMobileContext, DataSourceContext } from '../context/contexts.jsx'
// Sample tab icons - a sun for package holidays (flight + hotel) and a bed for
// hotel-only. Plain stroke SVGs (currentColor) so they pick up the tab's active
// /inactive colour; swap the paths for different artwork if preferred.
const sunIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);
const bedIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M2 4v16M2 9h16a3 3 0 0 1 3 3v8M2 16h19M6 9V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const SEARCH_TYPES = [
    { value: "packages", label: "Holidays", icon: sunIcon },
    { value: "stays", label: "Hotel Only", icon: bedIcon },
];
// Stable sentinel passed as departureAirports to the pickers when in "stays"
// mode - hotel only searches have no departure airports, but the pickers
// enable themselves (and re-fetch) based on that prop. The hotel only
// datasource ignores the value.
const STAY_ONLY_DEPS = ["stay-only"];
const SearchOverview = ({ departures, destination, datesText, openSearch, isMobile }) => {
    return <div className="">
        {isMobile && (
            <button onClick={() => openSearch()} type="button"
                className="btn-primary text-white p-2 inline-flex items-center justify-center rounded-full bg-emp">
                <span className="h-[32px] flex items-center justify-center px-4">Edit Search</span>
                <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.8043 11.25L12.9293 12.375M13.2543 14.2689C12.821 13.8356 12.821 13.1332 13.2543 12.7C13.6875 12.2667 14.3899 12.2667 14.8232 12.7L16.3543 14.2311C16.7876 14.6644 16.7876 15.3668 16.3543 15.8C15.9211 16.2333 15.2187 16.2333 14.7854 15.8L13.2543 14.2689ZM12.9293 7.125C12.9293 4.22551 10.5788 1.875 7.67932 1.875C4.77983 1.875 2.42932 4.22551 2.42932 7.125C2.42932 10.0245 4.77983 12.375 7.67932 12.375C10.5788 12.375 12.9293 10.0245 12.9293 7.125Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
            </button>
        )}
    </div>;
}

const OlympicSearch = ({ overviewNode, specificSearch, specificAccom, baseUrl, bookingEngineBaseUrl,showForm }) => {
    if (specificSearch && specificAccom) {
        throw new Error("cannot do both");
    }

    const [savedRooms, setSavedRooms, removeSavedRooms] = useJsonCookie('s_rooms', DEFAULT_ROOMS, baseUrl);
    const [savedDeparture, setSavedDeparture, removeSavedDeparture] = useJsonCookie("s_dep", null, baseUrl);
    const [savedDestination, setSavedDestination, removeSavedDestination] = useJsonCookie("s_dest", null, baseUrl);
    const [savedStartDate, setSavedStartDate, removeStartDate] = useJsonCookie("s_from_date", null, baseUrl);
    const [savedToDate, setSavedToDate, removeToDate] = useJsonCookie("s_to_date", null, baseUrl);
    const [savedSearchType, setSavedSearchType] = useJsonCookie("s_type", "packages", baseUrl);
    const [savedDateMode, setSavedDateMode] = useJsonCookie("s_date_mode", "dates", baseUrl);
    const [savedDuration, setSavedDuration] = useJsonCookie("s_duration", null, baseUrl);
    const [savedFlexDays, setSavedFlexDays] = useJsonCookie("s_flex", 0, baseUrl);

    const [searchType, setSearchType] = useState(specificSearch ? (specificSearch.type ?? "packages") : (savedSearchType ?? "packages"));
    const [rooms, setRooms] = useState(savedRooms);
    const [departures, setDepartures] = useState([]);
    // Multiple destinations can be selected; their codes are hyphen-joined into
    // the destination URL segment. Stored as an array of { name, code }.
    const [destinations, setDestinations] = useState([]);
    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);
    // "dates" = pick exact departure/return days; "month" = search the whole
    // month as a departure window with an explicit duration.
    const [dateMode, setDateMode] = useState(specificSearch ? "dates" : (savedDateMode ?? "dates"));
    const [selectedDuration, setSelectedDuration] = useState(null);
    // Fuzzy ± flexibility (in days) applied to the departure date in "dates"
    // mode; widens the earliest/latest start window in the booking URL.
    const [flexDays, setFlexDays] = useState(specificSearch ? 0 : (savedFlexDays ?? 0));
    const [overViewDepartureText, setOverViewDepartureText] = useState();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // Per-field validation messages, populated only on a failed submit so we
    // don't nag before the user has tried to search. Keys: departure,
    // destination, dates.
    const [errors, setErrors] = useState({});
    const { height, width } = useWindowDimensions();
    const isMobile = width < 1067;
    // Memoise so the datasource (and the context value below) keeps a stable
    // identity between renders - recreating it every render made every picker
    // re-render needlessly.
    const dataSource = useMemo(() => PackageDatasource.getDataSource(searchType, baseUrl), [searchType, baseUrl]);
    // Departure airports only exist for packages, and the departure picker
    // fetches its list once on mount (it stays mounted-but-hidden in stays
    // mode). If the widget mounts on the Hotel Only tab (saved s_type cookie)
    // the stays datasource would seed that one fetch with an empty list, and
    // flipping to Holidays then shows "No departure airports available" - so
    // the picker always gets the packages source.
    const packagesDataSource = useMemo(() => PackageDatasource.getDataSource("packages", baseUrl), [baseUrl]);
    // In stays mode the pickers receive a stable sentinel instead of real
    // departure airports (see STAY_ONLY_DEPS above).
    const effectiveDepartures = searchType == "stays" ? STAY_ONLY_DEPS : departures;

    const onSearchTypeChange = (type) => {
        if (type == searchType) {
            return;
        }
        setSearchType(type);
        setSavedSearchType(type);
        // Availability differs between the two indexes - the pickers
        // re-validate the destination themselves, but selected dates must be
        // cleared so a stale date cannot be submitted against the new type.
        setSelectedFromDate(null);
        setSelectedToDate(null);
        setSelectedDuration(null);
    };

    useEffect(() => {

        var departureToUse = savedDeparture;
        // The destination cookie may hold a legacy single object or the new
        // array - normalise to an array either way.
        var destinationsToUse = Array.isArray(savedDestination) ? savedDestination : (savedDestination ? [savedDestination] : []);
        var startDateToUse = savedStartDate ? parse(savedStartDate, "yyyy-MM-dd", new Date()) : null;
        var endDateToUse = savedToDate ? parse(savedToDate, "yyyy-MM-dd", new Date()) : null;
        if (specificSearch) {
            departureToUse = specificSearch.dep;
            destinationsToUse = specificSearch.to ? [specificSearch.to] : [];
            startDateToUse = parse(specificSearch.startDate, "yyyy-MM-dd", new Date());
            endDateToUse = parse(specificSearch.endDate, "yyyy-MM-dd", new Date());
            const roomsFromString = deserializeRooms(specificSearch.pax);
            setRooms(roomsFromString);

        } else if (specificAccom) {
            destinationsToUse = [specificAccom];
            setDestinations([specificAccom]);
        }

        const destinationCodes = destinationsToUse.map((d) => d.code).join('-');

        async function fetchData() {
            // A saved month search is a departure-date window, so validate it
            // against the whole range rather than the exact start date.
            var isMonthSearch = !specificSearch && savedDateMode == "month";
            var validateResponse = await dataSource.validate(departureToUse, destinationCodes, startDateToUse, endDateToUse, isMonthSearch);

            if (validateResponse.isValidDeparture) {
                setDepartures(departureToUse);
            }
            // Prefer the API-resolved destination list: it covers multi-code
            // segments (e.g. "AL-GR" -> Albania + Greece) and fills in names an
            // embedded booking URL doesn't carry. Fall back to the saved values.
            if (validateResponse.isValidDestination && !specificAccom) {
                if (validateResponse.destinations && validateResponse.destinations.length > 0) {
                    setDestinations(validateResponse.destinations.map((d) => ({ code: d.code, name: d.name })));
                } else {
                    setDestinations(destinationsToUse);
                }
            }
            if (validateResponse.isValidDates) {
                setSelectedFromDate(startDateToUse);
                setSelectedToDate(endDateToUse)
                if (isMonthSearch && savedDuration) {
                    setSelectedDuration(savedDuration);
                }
            }
        }
        var hasRequiredDeparture = searchType == "stays" || (departureToUse && departureToUse.length > 0);
        if (hasRequiredDeparture && destinationsToUse.length > 0 && startDateToUse && endDateToUse) {
            fetchData();
        }
    }, []);



    // Work out which required fields are missing/incomplete, with a message
    // that points at the specific field so the user knows what to fix.
    const validate = () => {
        const found = {};
        if (searchType != "stays" && (!departures || departures.length == 0)) {
            found.departure = "Choose a departure airport";
        }
        if (!destinations || destinations.length == 0) {
            found.destination = "Choose a destination";
        }
        if (!selectedFromDate) {
            found.dates = dateMode == "month" ? "Choose a month" : "Choose your dates";
        } else if (dateMode == "dates" && !selectedToDate) {
            // The common trap: an outbound date was picked but the popover was
            // closed before choosing a return.
            found.dates = "Choose a return date";
        } else if (dateMode == "month" && !selectedDuration) {
            found.dates = "Choose a trip length";
        }
        return found;
    };

    const submit = () => {
        const found = validate();
        setErrors(found);
        if (Object.keys(found).length > 0) {
            return;
        }

        if (searchType != "stays") {
            setSavedDeparture(departures);
        }


        if (!specificAccom) {
            setSavedDestination(destinations);
        }

        const fromDate = format(selectedFromDate, "yyyy-MM-dd");
        const toDate = format(selectedToDate, "yyyy-MM-dd");
        // Persist the user's literal picks so a reload restores what they chose
        // (the widened window below is only for the outgoing booking URL).
        setSavedStartDate(fromDate);
        setSavedToDate(toDate);
        setSavedRooms(rooms);
        // Remember the search type (packages / hotel-only) along with the date
        // mode, duration and ± fuzziness so the whole search restores on return.
        setSavedSearchType(searchType);
        setSavedDateMode(dateMode);
        setSavedDuration(dateMode == "month" ? selectedDuration : null);
        setSavedFlexDays(dateMode == "dates" ? flexDays : 0);

        // The booking engine takes a duration plus a start-date window
        // (earliest..latest start) - see helpers/bookingUrl.js. Earliest start
        // can never be before tomorrow.
        const { nights, earliestStart, latestStart } = computeStartWindow({
            dateMode,
            selectedFromDate,
            selectedToDate,
            selectedDuration,
            flexDays,
            earliestAllowedStart: startOfTomorrow(),
        });
        const roomsString = serializeRooms(rooms);
        // Hyphen-join the selected destination codes into the URL segment.
        const destinationCodes = destinations.map((d) => d.code).join('-');

        window.location.href = buildBookingUrl({
            bookingEngineBaseUrl,
            searchType,
            departures,
            destinationCodes,
            nights,
            earliestStart,
            latestStart,
            roomsString,
        });
    }

    // Clear (or refresh) each shown error as the user corrects a field - but
    // only once errors are already on screen, so we never flag a field before
    // the first submit attempt.
    useEffect(() => {
        setErrors((prev) => {
            if (Object.keys(prev).length === 0) return prev;
            const current = validate();
            const next = {};
            for (const key of Object.keys(prev)) {
                if (current[key]) next[key] = current[key];
            }
            return next;
        });
    }, [departures, destinations, selectedFromDate, selectedToDate, selectedDuration, dateMode, searchType]);

    let portalContent = null;


    window.toggleSearchForm = () => {
        setIsSearchOpen(!isSearchOpen);
    }

    var showSearch = showForm || (isSearchOpen || !isMobile);
    // Both search types are always offered; hotel-only is no longer gated.
    const visibleSearchTypes = SEARCH_TYPES;
    return (
        <IsMobileContext value={isMobile}>
            <DataSourceContext value={dataSource}>
                <div className={showSearch ? "" : "hidden"}>
                    {/* Search-type tabs sit above the white panel and flow into
                        it (folder-tab style): the active tab is the same white
                        as the panel so the two read as one continuous surface,
                        while the inactive tab sits behind in grey. The rounded
                        white background is no longer drawn behind the tab row -
                        the tabs stick out above it. */}
                    {visibleSearchTypes.length > 1 && (
                        <div className="flex gap-1 pl-6" role="tablist">
                            {visibleSearchTypes.map((type) => (
                                <button key={type.value} type="button" role="tab"
                                    aria-selected={searchType == type.value}
                                    onClick={() => onSearchTypeChange(type.value)}
                                    className={"px-5 py-2 text-base rounded-t-[14px] transition-colors flex items-center gap-2 " +
                                        (searchType == type.value
                                            ? "bg-white text-emp font-bold"
                                            : "bg-content_5 text-content_2 font-medium hover:text-primary")}>
                                    {type.icon}
                                    <span>{type.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="bg-white rounded-[24px] relative">
                        <div className="px-4 py-4">
                            <div className="flex flex-col lg:flex-row items-center justify-center gap-2.5">
                            {/* Departure Dropdown - kept mounted and just hidden
                                in stays mode (display:contents so it stays a flex
                                item when visible) so switching tabs doesn't
                                unmount/re-fetch it and flash "Loading...". */}
                            <div className={searchType == "stays" ? "hidden" : "contents"}>
                                <DataSourceContext value={packagesDataSource}>
                                    <DeparturePicker
                                        selectedAirports={departures}
                                        updateOverviewText={setOverViewDepartureText}
                                        onUpdate={setDepartures}
                                        error={errors.departure}
                                    />
                                </DataSourceContext>
                            </div>
                            <DestinationPicker
                                departureAirports={effectiveDepartures}

                                accomIsLocked={!!specificAccom}
                                selectedDestinations={destinations}
                                onUpdate={setDestinations}
                                error={errors.destination}
                            />
                            <DatePicker
                                departureAirports={effectiveDepartures}
                                destination={destinations.map((d) => d.code).join('-')}
                                error={errors.dates}

                                selectedFrom={selectedFromDate}
                                selectedTo={selectedToDate}
                                onChange={(from, to) => {
                                    setSelectedFromDate(from);
                                    setSelectedToDate(to);
                                }}
                                dateMode={dateMode}
                                onDateModeChange={setDateMode}
                                selectedDuration={selectedDuration}
                                onDurationChange={setSelectedDuration}
                                flexDays={flexDays}
                                onFlexDaysChange={setFlexDays}
                            />
                            <PaxPicker
                                rooms={rooms}
                                onChange={setRooms}
                            />

                            <div className="w-full lg:w-[150px]">
                                <button onClick={() => submit()} type="submit" className="group w-full p-[4px] bg-primary rounded-full pr-[34px] hover:pr-[4px] transition-all duration-200 relative">
                                    <span className="h-[32px] rounded-full bg-white flex items-center justify-center px-4 transition-all duration-200 group-hover:pr-[46px]">Search</span>
                                    <span className="absolute top-[4px] w-[32px] h-[32px] right-[4px] rounded-full bg-white text-primary flex items-center justify-center transition-all duration-200 group-hover:w-[42px] group-hover:pe-[10px]">
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15.5 15.0005L17 16.5005M17.4333 19.0257C16.8556 18.448 16.8556 17.5114 17.4333 16.9338C18.0109 16.3561 18.9475 16.3561 19.5252 16.9338L21.5667 18.9753C22.1444 19.553 22.1444 20.4896 21.5667 21.0672C20.9891 21.6449 20.0525 21.6449 19.4748 21.0672L17.4333 19.0257ZM17 9.50049C17 5.6345 13.866 2.50049 10 2.50049C6.13401 2.50049 3 5.6345 3 9.50049C3 13.3665 6.13401 16.5005 10 16.5005C13.866 16.5005 17 13.3665 17 9.50049Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
                {portalContent}
            </DataSourceContext>
        </IsMobileContext>);
};
export default OlympicSearch;
